import React, { useEffect, useState } from 'react';
import { X, Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { addDays, iso } from './useSchedule';

export function DaySheetModal({ open, onPreview, onSend, onClose }) {
  const [date, setDate] = useState(() => iso(addDays(new Date(), 1)));
  const [intro, setIntro] = useState('');
  const [msgs, setMsgs] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResults(null);
    const t = setTimeout(async () => {
      try {
        const r = await onPreview({ date, intro });
        setMsgs(r.messages);
        setPicked(r.messages.filter((m) => m.phone && m.jobs > 0).map((m) => m.crew_id));
      } catch (e) { toast.error(e.message); }
    }, 300);
    return () => clearTimeout(t);
  }, [open, date, intro, onPreview]);

  if (!open) return null;

  const send = async () => {
    if (!picked.length) return;
    setBusy(true);
    try {
      const r = await onSend({ date, intro, crew_ids: picked });
      setResults(r.results);
      const ok = r.results.filter((x) => x.sent).length;
      toast[ok ? 'success' : 'error'](`${ok} of ${r.results.length} day sheet${r.results.length === 1 ? '' : 's'} sent`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" data-testid="daysheet-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[95dvh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-bold text-gray-900 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#006400]" /> Send crew day sheet</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Close" data-testid="daysheet-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <p className="text-xs text-gray-500">Texts each crew member their jobs for the day — stage, customer, address and time. Nothing is sent until you press Send.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-gray-600">Day</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="daysheet-date" /></div>
            <div><Label className="text-xs font-semibold text-gray-600">Extra line (optional)</Label><Input value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="e.g. Skip arrives 7:30 - meet at the yard" className="mt-1" data-testid="daysheet-intro" /></div>
          </div>
          {msgs.length === 0 && <p className="text-sm text-gray-500 bg-gray-50 border border-dashed rounded-lg p-3 text-center" data-testid="daysheet-empty">No crew yet — add them from the Crew button first.</p>}
          {msgs.map((m) => {
            const on = picked.includes(m.crew_id);
            const res = results?.find((r) => r.crew_id === m.crew_id);
            return (
              <label key={m.crew_id} className={`block border rounded-xl p-3 ${on ? 'border-[#006400]/50 bg-[#006400]/5' : 'border-gray-200'}`} data-testid={`daysheet-row-${m.crew_id}`}>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={on} disabled={!m.phone} onChange={() => setPicked((p) => (on ? p.filter((i) => i !== m.crew_id) : [...p, m.crew_id]))} className="w-4 h-4" data-testid={`daysheet-pick-${m.crew_id}`} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.colour }} />
                  <span className="text-sm font-semibold text-gray-900">{m.name}</span>
                  <span className="text-xs text-gray-500">{m.phone || 'no mobile on file'} · {m.jobs} job{m.jobs === 1 ? '' : 's'}</span>
                  {res && <span className={`ml-auto text-xs font-semibold ${res.sent ? 'text-emerald-700' : 'text-red-600'}`} data-testid={`daysheet-result-${m.crew_id}`}>{res.sent ? 'Sent ✓' : res.error}</span>}
                </div>
                <pre className="mt-2 text-xs text-gray-700 bg-white border rounded-lg p-2.5 whitespace-pre-wrap font-sans">{m.body}</pre>
              </label>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t flex gap-2" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.75rem)' }}>
          <Button type="button" variant="outline" onClick={onClose} className="flex-1" data-testid="daysheet-cancel">Close</Button>
          <Button type="button" onClick={send} disabled={busy || !picked.length} className="flex-1 bg-[#006400] hover:bg-[#004d00] text-white" data-testid="daysheet-send">
            <Send className="w-4 h-4 mr-1.5" /> {busy ? 'Sending…' : `Send SMS to ${picked.length}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
